import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Clock, Disc3, FolderKanban, Layers3, Loader2, Mic2, Music4, RotateCcw, SlidersHorizontal, Trash2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import SongCover from "@/components/creator/SongCover";
import ProjectsTab from "@/components/creator/music/ProjectsTab";
import MixesTab from "@/components/creator/mix/MixesTab";
import { listMixes } from "@/lib/mixDeskApi";
import { deleteRecording, deleteSong, listRecordings, listSongs, restoreSong } from "@/lib/creatorApi";
import { proStudioPath, setCurrentSongId } from "@/lib/studioMode";
import { fmtBytesShort } from "@/lib/recordingStats";

const TABS = [
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "songs", label: "Songs", icon: Music4 },
  { key: "recordings", label: "Recordings", icon: Mic2 },
  { key: "stems", label: "Stems", icon: Layers3 },
  { key: "mixes", label: "Mixes", icon: SlidersHorizontal },
  { key: "masters", label: "Masters", icon: Disc3 },
  { key: "deleted", label: "Deleted", icon: Trash2 },
];

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)} d ago`;
  return d.toLocaleDateString();
};

function daysLeft(deletedAt) {
  const d = new Date(deletedAt).getTime();
  return Math.max(0, Math.ceil((d + 30 * 86400_000 - Date.now()) / 86400_000));
}

function Empty({ text, cta, to }) {
  return (
    <div data-testid="projects-empty" className="rounded-3xl border border-dashed border-white/10 p-10 text-center">
      <p className="text-white/55">{text}</p>
      {cta ? <Link to={to} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2.5 font-display text-sm font-semibold">{cta} <ArrowRight className="h-4 w-4" /></Link> : null}
    </div>
  );
}

function SongRow({ song, onOpen, onDelete, onRestore, onHardDelete, deleted, extra }) {
  return (
    <div data-testid={`project-song-${song.id}`} className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-3 transition-colors hover:bg-white/[0.04]">
      <SongCover song={song} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-semibold">{song.title}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-white/45">
          <span>{song.creator_meta?.style_label || song.style?.split(",")[0] || "Song"}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtWhen(song.created_at)}</span>
          {extra}
          {deleted && song.deleted_at ? <span className="text-amber-300/80">Gone in {daysLeft(song.deleted_at)} days</span> : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {deleted ? (
          <>
            <button type="button" data-testid={`project-restore-${song.id}`} onClick={() => onRestore(song)} className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/20"><RotateCcw className="h-3 w-3" /> Restore</button>
            <button type="button" data-testid={`project-hard-delete-${song.id}`} onClick={() => onHardDelete(song)} className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/55 hover:text-rose-300">Delete forever</button>
          </>
        ) : (
          <>
            <button type="button" data-testid={`project-open-${song.id}`} onClick={() => onOpen(song)} className="rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white">Open</button>
            <button type="button" data-testid={`project-delete-${song.id}`} onClick={() => onDelete(song)} aria-label="Delete" className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/50 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
}

/** My Projects — Songs · Recordings · Stems · Mixes · Masters · Deleted. */
export default function MyProjectsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projectCount, setProjectCount] = useState(null);
  const [mixCount, setMixCount] = useState(null);
  const [songs, setSongs] = useState(null);
  const [deleted, setDeleted] = useState(null);
  const [recordings, setRecordings] = useState(null);

  const load = useCallback(async () => {
    const [s, d, r, m] = await Promise.all([
      listSongs({ limit: 200 }).catch(() => []),
      listSongs({ deleted: true, limit: 200 }).catch(() => []),
      listRecordings().catch(() => []),
      listMixes().catch(() => []),
    ]);
    setSongs(s); setDeleted(d); setRecordings(r); setMixCount(m.length);
  }, []);
  useEffect(() => { load(); }, [load]);

  const savedMix = useMemo(() => {
    try { const raw = localStorage.getItem("elpm-session"); return raw ? JSON.parse(raw) : null; } catch (e) { void e; return null; }
  }, []);

  const open = (song) => { setCurrentSongId(song.id); navigate(`/create/song/${song.id}`); };
  const softDelete = async (song) => {
    try { await deleteSong(song.id); toast.message(`"${song.title}" moved to Deleted`, { description: "Kept for 30 days — restore anytime." }); load(); }
    catch (e) { toast.error("Couldn't delete", { description: e?.message }); }
  };
  const restore = async (song) => {
    try { await restoreSong(song.id); toast.success(`"${song.title}" restored`); load(); }
    catch (e) { toast.error("Couldn't restore", { description: e?.message }); }
  };
  const hardDelete = async (song) => {
    if (!window.confirm(`Delete "${song.title}" forever? This cannot be undone.`)) return;
    try { await deleteSong(song.id, { hard: true }); toast.message("Deleted forever"); load(); }
    catch (e) { toast.error("Couldn't delete", { description: e?.message }); }
  };
  const removeRecording = async (rec) => {
    if (!window.confirm("Delete this recording?")) return;
    try { await deleteRecording(rec.id); toast.message("Recording deleted"); load(); }
    catch (e) { toast.error("Couldn't delete", { description: e?.message }); }
  };

  const counts = {
    projects: projectCount ?? "…",
    songs: songs?.length ?? 0,
    recordings: recordings?.length ?? 0,
    stems: songs?.filter((s) => s.stems?.length).length ?? 0,
    mixes: (mixCount ?? 0) + (savedMix ? 1 : 0),
    masters: songs?.filter((s) => s.master).length ?? 0,
    deleted: deleted?.length ?? 0,
  };
  const loading = songs === null;

  const body = () => {
    if (tab === "projects") return <ProjectsTab onCount={setProjectCount} />;
    if (loading) return <div className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading your projects…</div>;
    if (tab === "songs") return songs.length ? <div className="grid gap-2">{songs.map((s) => <SongRow key={s.id} song={s} onOpen={open} onDelete={softDelete} />)}</div> : <Empty text="No songs yet." cta="Create your first song" to="/create/song" />;
    if (tab === "stems") {
      const rows = songs.filter((s) => s.stems?.length);
      return rows.length ? <div className="grid gap-2">{rows.map((s) => <SongRow key={s.id} song={s} onOpen={open} onDelete={softDelete} extra={<span className="text-cyan-200/80">{s.stems.length} parts</span>} />)}</div> : <Empty text="No separated songs yet. Open a song and press Separate Stems." cta="Go to Songs" to="/create/projects" />;
    }
    if (tab === "masters") {
      const rows = songs.filter((s) => s.master);
      return rows.length ? <div className="grid gap-2">{rows.map((s) => <SongRow key={s.id} song={s} onOpen={(x) => navigate(`/create/master/${x.id}`)} onDelete={softDelete} extra={<span className="text-emerald-200/80">{s.master.label} · {fmtWhen(s.master.at)}</span>} />)}</div> : <Empty text="Nothing mastered yet. Open a song and choose Improve the Mix." cta="Master a song" to="/create/master" />;
    }
    if (tab === "deleted") return deleted.length ? <div className="grid gap-2">{deleted.map((s) => <SongRow key={s.id} song={s} deleted onRestore={restore} onHardDelete={hardDelete} />)}</div> : <Empty text="Nothing in Deleted. Songs you remove stay here for 30 days." />;
    if (tab === "mixes") {
      const legacy = savedMix ? (
        <div data-testid="project-mix" className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4">
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500"><SlidersHorizontal className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold">Advanced Studio session · {(savedMix.channels || []).filter((c) => c.hasAudio).length} channels</p>
            <p className="text-[11px] text-white/45">Saved {fmtWhen(savedMix.savedAt)} · {savedMix.tempo || "—"} BPM · mixer, effects and automation</p>
          </div>
          <Link to={proStudioPath(null, "session")} data-testid="project-mix-open" className="rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white">Open in Advanced Studio</Link>
        </div>
      ) : null;
      return <MixesTab onCount={setMixCount} legacy={legacy} />;
    }
    // recordings
    return recordings.length ? (
      <div className="grid gap-2" data-testid="project-recordings">
        {recordings.map((rec) => {
          const files = Object.keys(rec.files || {}).length;
          return (
            <div key={rec.id} data-testid={`project-recording-${rec.id}`} className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-3">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-rose-500 to-red-500"><Mic2 className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-semibold">{rec.label || `Recording ${String(rec.id).slice(0, 8)}`}</p>
                <p className="text-[11px] text-white/45">{fmtWhen(rec.started_at)} · {files} take{files === 1 ? "" : "s"} · {fmtBytesShort(rec.bytes)}</p>
              </div>
              <Link to={proStudioPath(null, "recordings")} data-testid={`project-recording-open-${rec.id}`} className="rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white">Open</Link>
              <button type="button" data-testid={`project-recording-delete-${rec.id}`} onClick={() => removeRecording(rec)} aria-label="Delete recording" className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/50 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>
    ) : <Empty text="No recordings yet. Record your voice over a song to see takes here." cta="Record my voice" to={proStudioPath(null, "record")} />;
  };

  return (
    <CreatorShell testId="my-projects-page" back="/create" title="My Music Library" subtitle="Every project, version, recording and master — pick up where you left off." wide>
      <div role="tablist" data-testid="projects-tabs" className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={active} data-testid={`projects-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${active ? "border-transparent bg-gradient-to-r from-blue-500 to-violet-600 text-white" : "border-white/10 bg-white/[0.03] text-white/60 hover:text-white"}`}>
              <Icon className="h-3.5 w-3.5" /> {t.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? "bg-white/20" : "bg-white/[0.06]"}`} data-testid={`projects-count-${t.key}`}>{counts[t.key]}</span>
            </button>
          );
        })}
      </div>
      <div data-testid={`projects-panel-${tab}`}>{body()}</div>
    </CreatorShell>
  );
}
